import { useCallback, useMemo, useState } from 'react'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useToast } from '@/components/ui/toast'
import MoreIcon from '@/modules/chatting/icons/more.svg'
import type { ListUser } from '@/modules/chatting/types'
import { IconButton } from '@/modules/chatting/ui/components/icon-button'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar'
import { classNames } from '@/utils/helpers'

interface Props {
  user: ListUser
  onClickDiscount?: () => void
  onClickRename?: () => void
  onClickEditNotes?: () => void
  onClickEditLists?: () => void
  onClickRestrict?: () => void
  onClickBlock?: () => void
  isBlockLoading?: boolean
}

interface ActionType {
  label: string
  onClick: () => void
  isLoading?: boolean
}

export const MoreUserActions = ({
  user,
  onClickDiscount,
  onClickRename,
  onClickEditNotes,
  onClickEditLists,
  onClickRestrict,
  onClickBlock,
  isBlockLoading,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const toast = useToast()

  const handleAction = (action: () => void) => () => {
    action()
    setIsOpen(false)
  }

  const handleClickCopyLink = useCallback(() => {
    const profileLink = `https://onlyfans.com/${user.username}`

    navigator.clipboard.writeText(profileLink)
    toast.success('Link copied to clipboard')
  }, [user.username, toast])

  const actions: ActionType[][] = useMemo(
    () =>
      [
        [
          {
            label: 'Copy link to profile',
            onClick: handleClickCopyLink,
          },
          onClickRename && { label: 'Rename user', onClick: onClickRename },
          onClickEditLists && {
            label: 'Add to / remove from list',
            onClick: onClickEditLists,
          },
          onClickDiscount && {
            label: 'Give user a discount',
            onClick: onClickDiscount,
          },
          onClickEditNotes && {
            label: 'Edit notes',
            onClick: onClickEditNotes,
          },
        ],
        [
          {
            label: user.isRestricted ? 'Unrestrict' : 'Restrict',
            onClick: onClickRestrict,
          },
          {
            label: user.isBlocked ? 'Unblock' : 'Block',
            onClick: onClickBlock,
            isLoading: isBlockLoading,
          },
        ],
      ].map((group) =>
        group.filter((action): action is ActionType => !!action),
      ),
    [
      onClickRename,
      onClickEditLists,
      onClickDiscount,
      onClickEditNotes,
      onClickRestrict,
      onClickBlock,
      user.isRestricted,
      user.isBlocked,
      handleClickCopyLink,
      isBlockLoading,
    ],
  )

  return (
    <Popover
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <PopoverTrigger asChild>
        <IconButton
          icon={MoreIcon}
          className="text-secondary-300"
        />
      </PopoverTrigger>
      <PopoverContent className="z-[70] max-w-[206px] overflow-hidden p-0">
        <div className="flex gap-2 border-b px-5 py-1.5">
          <CreatorAvatar
            size={22}
            avatarSrc={
              user.avatarThumbs?.c50 ?? user.avatarThumbs?.c144 ?? user.avatar
            }
            name={user.displayName ?? user.name}
            className="shrink-0"
            fallbackClassName="!text-[8px]"
          />
          <p className="text-sm font-medium">@{user.username}</p>
        </div>
        {actions.map((group, index) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            className={classNames({
              'border-b': index !== actions.length - 1,
            })}
          >
            {group.map((action) => (
              <button
                key={action.label}
                onClick={handleAction(action.onClick)}
                disabled={action.isLoading}
                className={classNames(
                  'block w-full px-5 py-1.5 text-left text-sm hover:bg-secondary-50 dark:hover:bg-secondary-600',
                  {
                    'cursor-not-allowed opacity-50': action.isLoading,
                  },
                )}
              >
                {action.label}
              </button>
            ))}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  )
}
